function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
import React from "react";
import { get, includes, throttle } from "lodash";
import { Portal } from "react-portal";
const CHECKING_UPDATE_INTERVAL = 20;
const UPDATING_POSITION_INTERVAL = 50;
const computePosition = (pos, opts)=>{
    const { targetRect, rect } = opts;
    let { left = 0, top = 0 } = targetRect || {};
    const { height = 0, width = 0 } = targetRect || {};
    if (pos === 'top') {} else if (pos === 'bottom') {
        top += height;
    } else if (pos === 'left') {
        top += height / 2;
    } else if (pos === 'right') {
        left += width;
        top += height / 2;
    } else {}
    const portalWidth = rect == null ? void 0 : rect.width;
    if (portalWidth && left + portalWidth > window.innerWidth) {
        const delta = left + portalWidth - window.innerWidth;
        left -= delta;
    }
    return {
        left,
        top,
        widthParent: width
    };
};
class StickyPortal extends React.Component {
    componentDidMount() {
        const { refreshEventOff, refreshEventOn } = this.props;
        refreshEventOff && refreshEventOn && refreshEventOn(this.updatePosition);
        this.updatePosition();
        !!window && window.addEventListener('resize', this.delayedUpdate);
        this._checkForUpdate = setInterval(this.shouldPositionUpdate, CHECKING_UPDATE_INTERVAL);
    }
    componentDidUpdate(prevProps) {
        const { isOpened } = this.props;
        if (prevProps.isOpened !== isOpened) {
            this.updatePosition();
        }
    }
    componentWillUnmount() {
        const { refreshEventOff } = this.props;
        refreshEventOff && refreshEventOff(this.updatePosition);
        !!window && window.removeEventListener('resize', this.delayedUpdate);
        if (this._checkForUpdate) {
            clearInterval(this._checkForUpdate);
        }
    }
    getRelativeAncestor() {
        if (!this._root) {
            return null;
        }
        let parent = this._root.parentElement;
        while(get(parent, 'style.position') !== 'relative' && !!window && parent !== window.document.body){
            var _parent_parentElement;
            parent = (_parent_parentElement = parent == null ? void 0 : parent.parentElement) != null ? _parent_parentElement : null;
        }
        return parent;
    }
    render() {
        const { className, children, isOpened } = this.props;
        return /*#__PURE__*/ React.createElement("div", {
            ref: this.setRef
        }, isOpened && /*#__PURE__*/ React.createElement(Portal, null, /*#__PURE__*/ React.createElement("div", {
            className: className,
            ref: this.setChildrenRef,
            style: this.getStyle()
        }, children)));
    }
    constructor(...args){
        super(...args);
        this._checkForUpdate = undefined;
        this._childrenRef = null;
        this._root = null;
        this.state = {
            isFirstRender: false,
            previousLeftPosition: undefined,
            previousTopPosition: undefined,
            x: 0,
            y: 0,
            width: undefined
        };
        this.handleWidth = ()=>{
            const { style } = this.props;
            const { width } = this.state;
            const styleWidth = get(style, 'width', '100%');
            return styleWidth === '100%' ? width : styleWidth;
        };
        this.handlePositioner = (pos)=>{
            const { style } = this.props;
            const stylePos = get(style, pos);
            if (stylePos) {
                if (includes([
                    '100%',
                    'auto'
                ], stylePos)) {
                    return 0;
                }
                return stylePos;
            }
            return 0;
        };
        this.setRef = (el)=>{
            const { ref } = this.props;
            ref && ref(el);
            this._root = el;
        };
        this.setChildrenRef = (n)=>{
            this._childrenRef = n;
        };
        this.getStyle = ()=>{
            const { style, zIndex } = this.props;
            const { isFirstRender, x, y } = this.state;
            let resultStyle = _extends({}, style, {
                width: this.handleWidth(),
                position: 'absolute',
                top: this.handlePositioner('top'),
                left: this.handlePositioner('left'),
                transform: `translateX(${x}px) translateY(${y}px)`,
                zIndex
            });
            if (!isFirstRender) {
                resultStyle = _extends({}, resultStyle, {
                    transition: 'transform 25ms ease'
                });
            }
            return resultStyle;
        };
        this.shouldPositionUpdate = ()=>{
            if (!this.props.isOpened || !this._childrenRef) {
                return;
            }
            const refNode = this.getRelativeAncestor();
            if (!refNode) {
                return;
            }
            const targetBoundingRect = refNode.getBoundingClientRect();
            const { left, top } = targetBoundingRect;
            const { previousLeftPosition, previousTopPosition } = this.state;
            if (previousLeftPosition !== left || previousTopPosition !== top) {
                this.updatePosition(targetBoundingRect);
            }
        };
        this.delayedUpdate = throttle(()=>this.updatePosition(), UPDATING_POSITION_INTERVAL);
        this.updatePosition = (targetBoundingRect)=>{
            let refNode;
            const getTargetRect = ()=>{
                if (!targetBoundingRect) {
                    if (!this.props.isOpened) {
                        return;
                    }
                    if (!this._childrenRef) {
                        return;
                    }
                    refNode = this.getRelativeAncestor();
                    if (!refNode) {
                        return;
                    }
                    return refNode.getBoundingClientRect();
                }
                return targetBoundingRect;
            };
            const { position } = this.props;
            const targetRect = getTargetRect();
            if (!targetRect) {
                return;
            }
            const rect = this._childrenRef ? this._childrenRef.getBoundingClientRect() : undefined;
            const finalPos = computePosition(position, {
                rect,
                targetRect
            });
            let nextState = {
                previousLeftPosition: targetRect == null ? void 0 : targetRect.left,
                previousTopPosition: targetRect == null ? void 0 : targetRect.top,
                x: Math.round(finalPos.left + (!!window && window.pageXOffset ? window.pageXOffset : 0)),
                y: Math.round(finalPos.top + (!!window && window.pageYOffset ? window.pageYOffset : 0)),
                width: finalPos.widthParent
            };
            const { isFirstRender } = this.state;
            if (!isFirstRender) {
                nextState = _extends({}, nextState, {
                    isFirstRender: true
                });
            } else if (isFirstRender) {
                nextState = _extends({}, nextState, {
                    isFirstRender: false
                });
            }
            this.setState(nextState);
        };
    }
}
StickyPortal.defaultProps = {
    zIndex: 9000
};
export default StickyPortal;
